import { useMemo, useState } from 'react';
import { useEntitlementsUsage } from '@/hooks/account/useBilling';
import {
  estimatedPages,
  fileLimit,
  pagesPerMinute,
  parseRanges,
  scopeUnitOf,
  selectionBudget,
  sheetRowsSelection,
  type PageRange,
  type ParsedRanges,
  type ScopeUnit,
} from '@/lib/sources/scopeSelection';
import type { OutlineSection, SourceItem } from '@/types/api';
import { usePartSelection } from './usePartSelection';

export interface ScopeSubmission {
  section_ids?: string[];
  page_ranges?: PageRange[];
  sheet_rows?: Record<string, PageRange>;
}

/** Each ticked sheet's row range (spreadsheets), and what it keeps. */
function sheetChoice(
  sections: OutlineSection[],
  selected: Set<string>,
  rowText: Record<string, string>
) {
  let cells = 0;
  let error: string | null = null;
  const ranges: Record<string, PageRange> = {};
  for (const s of sections) {
    if (!selected.has(s.id)) continue;
    const pick = sheetRowsSelection(s, rowText[s.id] ?? '');
    cells += pick.cells;
    if (pick.error && !error) error = `${s.title}: ${pick.error}`;
    if (pick.range) ranges[s.id] = pick.range;
  }
  return { cells, error, ranges };
}

/** Typed pages/slides ("1-30, 45"); spreadsheets choose sheets only. */
function useTypedRanges(unit: ScopeUnit, total: number | null) {
  const [text, setText] = useState('');
  const typed: ParsedRanges | null =
    unit === 'cell' || !text.trim() ? null : parseRanges(text, total);
  return { text, setText, typed };
}

interface Totals {
  unit: ScopeUnit;
  /** A video's pages per minute (null for other sources). */
  perMinute: number | null;
  typed: ParsedRanges | null;
  /** Parts chosen: their size, and an error from a sheet's row range. */
  parts: { units: number; count: number; error: string | null };
  limit: number | null;
  pagesLeft: number | null;
}

/** What the current choice costs, and whether it can be sent. */
function totals({ unit, perMinute, typed, parts, limit, pagesLeft }: Totals) {
  const units = typed ? typed.units : parts.units;
  const estPages = estimatedPages(unit, units, perMinute);
  const overFile = limit !== null && units > limit;
  const overMonth = pagesLeft !== null && estPages > pagesLeft;
  const valid = typed ? !typed.error : parts.count > 0 && !parts.error;
  return {
    selectedUnits: units,
    estPages,
    overFile,
    overMonth,
    canSubmit: valid && units > 0 && !overFile && !overMonth,
  };
}

/**
 * State for the source picker: which parts are chosen (or which ranges were
 * typed), what that costs against the file limit and the month's pages, and
 * whether it can be sent. Works the same for pages, slides and sheets.
 */
export function useScopePicker(source: SourceItem) {
  const unit = scopeUnitOf(source);
  const sections = useMemo(
    () => source.outline?.sections ?? [],
    [source.outline]
  );
  const total =
    source.size_units ??
    source.outline?.total_units ??
    source.page_count ??
    null;
  const limit = fileLimit(source, unit);
  const pagesLeft =
    useEntitlementsUsage().data?.pages_parsed?.remaining ?? null;
  const perMinute = unit === 'minute' ? pagesPerMinute(source) : null;
  const parts = usePartSelection(
    sections,
    selectionBudget(unit, limit, pagesLeft, perMinute)
  );
  const ranges = useTypedRanges(unit, total);
  const { typed } = ranges;
  const [rowText, setRowText] = useState<Record<string, string>>({});
  const sheets = sheetChoice(sections, parts.selected, rowText);
  const partUnits =
    unit === 'cell'
      ? sheets.cells
      : sections.reduce(
          (n, s) => (parts.selected.has(s.id) ? n + s.est_units : n),
          0
        );

  return {
    unit,
    sections,
    total,
    limit,
    pagesLeft,
    selected: parts.selected,
    toggle: parts.toggle,
    setAll: parts.setAll,
    rangeText: ranges.text,
    typeRanges: (text: string) => {
      parts.markTouched();
      ranges.setText(text);
    },
    rangeError: typed?.error ?? null,
    usingRanges: typed !== null,
    rowText,
    setRows: (id: string, text: string) => {
      parts.markTouched();
      setRowText(prev => ({ ...prev, [id]: text }));
    },
    rowsError: unit === 'cell' ? sheets.error : null,
    ...totals({
      unit,
      perMinute,
      typed,
      parts: {
        units: partUnits,
        count: parts.selected.size,
        error: unit === 'cell' ? sheets.error : null,
      },
      limit,
      pagesLeft,
    }),
    submission: (): ScopeSubmission => {
      if (typed) return { page_ranges: typed.ranges };
      const ids = [...parts.selected];
      return Object.keys(sheets.ranges).length
        ? { section_ids: ids, sheet_rows: sheets.ranges }
        : { section_ids: ids };
    },
  };
}

export type ScopePicker = ReturnType<typeof useScopePicker>;
